<template>
    <div>
        <div v-show="activeSubTab === 'all'">
            <MentionTable
                ref="table"
                :isActive="activeSubTab === 'all'"
                :brand="brand"
                :brands="brands"
                :sentimentAvailable="sentimentAvailable"
                :initialFilters="initialFilters"
                :initialFiltersReady="initialFiltersReady"
                :tags="tags"
                :tagOperator="tagOperator"
                :search="search"
                :hasTags="hasTags"
                :platforms="platforms"
                :project="project"
                :selectedPlatform="effectiveSelectedPlatform"
                :selectedDates="selectedDates"
                :from-date="fromDate"
                :to-date="toDate"
                :project-id="projectId"
                :loading="loading"
                @filtersUpdated="$emit('filtersUpdated', $event)" />
        </div>

        <div v-show="activeSubTab === 'new'">
            <v-data-table
                id="mentionTableDeltaNew"
                :headers="deltaHeaders"
                :items="filteredNewMentionsRows"
                :loading="deltaLoading"
                @click:row="onRowInteraction"
                class="elevation-0"
                fixed-header
                height="70vh"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }">
                <template v-slot:item.platform="{ item }">
                    <span class="platform-icon-wrapper" v-if="getPlatformInfo(item.platform).logo" style="display: flex; align-items: center">
                        <span class="platform-icon" v-html="getPlatformInfo(item.platform).logo" style="margin: auto"></span>
                    </span>
                    <span v-else>
                        {{ item.platform }}
                    </span>
                </template>
                <template v-slot:item.prompt="{ item }">
                    <span style="white-space: pre-line">{{ item.prompt }}</span>
                </template>
                <template v-slot:item.mention="{ item }">
                    <span style="max-width: 500px" v-html="renderMentionMarkdown(item)"></span>
                </template>
                <template v-slot:item.importDate="{ item }">
                    {{ formatDate(item.importDate) }}
                </template>
                <template v-slot:item.brands="{ item }">
                    <div class="entity-chip-list">
                        <v-chip v-for="(label, idx) in getVisibleBrands(item.brandsText)" :key="label + '-' + idx" small outlined class="entity-chip ma-1" :style="entityChipStyle(label)">
                            <span class="entity-chip-dot" :style="entityDotStyle(label)"></span>
                            <span class="entity-chip-label">{{ label }}</span>
                        </v-chip>
                        <v-tooltip bottom v-if="getHiddenBrands(item.brandsText).length">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip small outlined class="entity-chip-more ma-1" color="grey darken-1" text-color="grey darken-1" v-bind="attrs" v-on="on">
                                    +{{ getHiddenBrands(item.brandsText).length }}
                                </v-chip>
                            </template>
                            <div class="entity-more-tooltip">
                                <div class="entity-more-tooltip-row" v-for="(label, i) in getHiddenBrands(item.brandsText)" :key="label + '-' + i">
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:item.sources="{ item }">
                    <div class="d-flex flex-wrap" style="max-width: 300px; align-items: center">
                        <v-tooltip bottom v-for="(src, i) in sortedSources(item.sources)" :key="src._key || i">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    outlined
                                    class="ma-1"
                                    color="grey darken-1"
                                    text-color="grey darken-1"
                                    @click.stop
                                    :href="src.url || null"
                                    target="_blank"
                                    rel="noopener">
                                    {{ src.domain || "—" }}
                                </v-chip>
                            </template>
                            <span>{{ src.url || src.key || src.domain }}</span>
                        </v-tooltip>
                        <span v-if="extraSourceCount(item.sources) > 0" class="grey--text caption ma-1">+{{ extraSourceCount(item.sources) }}</span>
                    </div>
                </template>
            </v-data-table>
        </div>

        <div v-show="activeSubTab === 'lost'">
            <v-data-table
                id="mentionTableDeltaLost"
                :headers="deltaHeaders"
                :items="filteredLostMentionsRows"
                :loading="deltaLoading"
                @click:row="onRowInteraction"
                class="elevation-0"
                fixed-header
                height="70vh"
                :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }">
                <template v-slot:item.platform="{ item }">
                    <span class="platform-icon-wrapper" v-if="getPlatformInfo(item.platform).logo" style="display: flex; align-items: center">
                        <span class="platform-icon" v-html="getPlatformInfo(item.platform).logo" style="margin: auto"></span>
                    </span>
                    <span v-else>
                        {{ item.platform }}
                    </span>
                </template>
                <template v-slot:item.prompt="{ item }">
                    <span style="white-space: pre-line">{{ item.prompt }}</span>
                </template>
                <template v-slot:item.mention="{ item }">
                    <span style="max-width: 500px" v-html="renderMentionMarkdown(item)"></span>
                </template>
                <template v-slot:item.importDate="{ item }">
                    {{ formatDate(item.importDate) }}
                </template>
                <template v-slot:item.brands="{ item }">
                    <div class="entity-chip-list">
                        <v-chip v-for="(label, idx) in getVisibleBrands(item.brandsText)" :key="label + '-' + idx" small outlined class="entity-chip ma-1" :style="entityChipStyle(label)">
                            <span class="entity-chip-dot" :style="entityDotStyle(label)"></span>
                            <span class="entity-chip-label">{{ label }}</span>
                        </v-chip>
                        <v-tooltip bottom v-if="getHiddenBrands(item.brandsText).length">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip small outlined class="entity-chip-more ma-1" color="grey darken-1" text-color="grey darken-1" v-bind="attrs" v-on="on">
                                    +{{ getHiddenBrands(item.brandsText).length }}
                                </v-chip>
                            </template>
                            <div class="entity-more-tooltip">
                                <div class="entity-more-tooltip-row" v-for="(label, i) in getHiddenBrands(item.brandsText)" :key="label + '-' + i">
                                    <span>{{ label }}</span>
                                </div>
                            </div>
                        </v-tooltip>
                    </div>
                </template>
                <template v-slot:item.sources="{ item }">
                    <div class="d-flex flex-wrap" style="max-width: 300px; align-items: center">
                        <v-tooltip bottom v-for="(src, i) in sortedSources(item.sources)" :key="src._key || i">
                            <template v-slot:activator="{ on, attrs }">
                                <v-chip
                                    v-bind="attrs"
                                    v-on="on"
                                    small
                                    outlined
                                    class="ma-1"
                                    color="grey darken-1"
                                    text-color="grey darken-1"
                                    @click.stop
                                    :href="src.url || null"
                                    target="_blank"
                                    rel="noopener">
                                    {{ src.domain || "—" }}
                                </v-chip>
                            </template>
                            <span>{{ src.url || src.key || src.domain }}</span>
                        </v-tooltip>
                        <span v-if="extraSourceCount(item.sources) > 0" class="grey--text caption ma-1">+{{ extraSourceCount(item.sources) }}</span>
                    </div>
                </template>
            </v-data-table>
        </div>
    </div>
</template>

<script>
    import MentionTable from "../../../components/aisuite/MentionTable.vue"
    import { getBrandColorFromName } from "../../../utils/brandColor"
    import MarkdownIt from "markdown-it"

    export default {
        name: "AIProjectMentionsView",
        inject: {
            // Von AIProject bereitgestellt: öffnet das Prompt-Detail als Overlay statt per Route
            aiSuiteOpenPromptDetail: { default: null }
        },
        components: { MentionTable },
        props: {
            activeSubTab: { type: String, default: "all" },
            brand: { type: Object, default: () => ({}) },
            brands: { type: Array, default: () => [] },
            sentimentAvailable: { type: Boolean, default: false },
            initialFilters: { type: Array, default: () => [] },
            initialFiltersReady: { type: Boolean, default: false },
            tags: { type: Array, default: () => [] },
            tagOperator: { type: String, default: "or" },
            search: { type: String, default: "" },
            hasTags: { type: Boolean, default: false },
            platforms: { type: Array, default: () => [] },
            project: { type: Object, default: null },
            selectedPlatform: { type: [Object, String], default: null },
            selectedDates: { type: Array, default: () => [] },
            fromDate: { type: String, default: "" },
            toDate: { type: String, default: "" },
            projectId: { type: [String, Number], default: "" },
            loading: { type: Boolean, default: false },
            platformFilterKey: { type: String, default: null },
            platformFilterMetric: { type: String, default: null },
            cachedDeltaSignature: { type: String, default: "" }
        },
        data() {
            return {
                deltaLoading: false,
                totalMentionsCount: 0,
                newMentionsRows: [],
                lostMentionsRows: [],
                platformDeltaRows: [],
                _deltaDebounce: null,
                md: new MarkdownIt()
            }
        },
        computed: {
            deltaInputSignature() {
                try {
                    return JSON.stringify({
                        projectId: this.projectId,
                        brand: this.brand?._id || this.brand?.brand || this.brand?.name || "",
                        fromDate: this.fromDate,
                        toDate: this.toDate,
                        selectedPlatform: this.selectedPlatform,
                        tags: this.tags,
                        tagOperator: this.tagOperator,
                        initialFilters: this.initialFilters,
                        initialFiltersReady: this.initialFiltersReady
                    })
                } catch (e) {
                    return ""
                }
            },
            effectiveSelectedPlatform() {
                if (this.platformFilterKey && this.activeSubTab === "all") {
                    const info = this.getPlatformInfo(this.platformFilterKey) || {}
                    return { key: this.platformFilterKey, name: info.label || this.platformFilterKey }
                }
                return this.selectedPlatform
            },
            filteredNewMentionsRows() {
                if (!this.platformFilterKey || this.activeSubTab !== "new") return this.newMentionsRows
                return this.newMentionsRows.filter((row) => (row?.platform || "").toString().toLowerCase() === this.platformFilterKey)
            },
            filteredLostMentionsRows() {
                if (!this.platformFilterKey || this.activeSubTab !== "lost") return this.lostMentionsRows
                return this.lostMentionsRows.filter((row) => (row?.platform || "").toString().toLowerCase() === this.platformFilterKey)
            },
            deltaHeaders() {
                return [
                    { text: "Platform", value: "platform", align: "start", filterable: false },
                    { text: "Prompt", value: "prompt", align: "start", filterable: true, width: "350px" },
                    { text: "Mention", value: "mention", align: "start", filterable: true, width: "400px" },
                    { text: "Brands", value: "brands", class: "col-brands", cellClass: "col-brands", width: "300px", minWidth: "320px", align: "start", filterable: true },
                    { text: "Sources", value: "sources", align: "start", filterable: true },
                    { text: "Updated", value: "importDate", align: "start", filterable: false }
                ]
            }
        },
        watch: {
            fromDate() {
                this.scheduleDeltaRefresh()
            },
            toDate() {
                this.scheduleDeltaRefresh()
            },
            selectedPlatform: {
                deep: true,
                handler() {
                    this.scheduleDeltaRefresh()
                }
            },
            tags: {
                deep: true,
                handler() {
                    this.scheduleDeltaRefresh()
                }
            },
            tagOperator() {
                this.scheduleDeltaRefresh()
            },
            initialFilters: {
                deep: true,
                handler() {
                    this.scheduleDeltaRefresh()
                }
            },
            initialFiltersReady(newVal) {
                if (newVal) this.scheduleDeltaRefresh()
            },
            projectId() {
                this.scheduleDeltaRefresh()
            },
            brand: {
                deep: true,
                handler() {
                    this.scheduleDeltaRefresh()
                }
            }
        },
        methods: {
            scheduleDeltaRefresh() {
                clearTimeout(this._deltaDebounce)
                this._deltaDebounce = setTimeout(() => {
                    this.fetchMentionDeltas()
                }, 220)
            },
            formatDate(date) {
                if (!date) return ""
                const d = new Date(date)
                return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString()
            },
            splitCsv(text) {
                return (text || "")
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
            },
            getVisibleBrands(text) {
                return this.splitCsv(text).slice(0, 4)
            },
            getHiddenBrands(text) {
                return this.splitCsv(text).slice(4)
            },
            entityDotStyle(label) {
                const color = getBrandColorFromName(this.normalizeToken(label))
                return {
                    backgroundColor: color
                }
            },
            entityChipStyle(label) {
                const color = getBrandColorFromName(this.normalizeToken(label))
                return {
                    borderColor: `${color}66`,
                    backgroundColor: `${color}10`
                }
            },
            getPlatformInfo(name) {
                let ctx = this.$parent
                while (ctx) {
                    if (typeof ctx.getPlatformInfo === "function") return ctx.getPlatformInfo(name)
                    ctx = ctx.$parent
                }
                return { logo: null, label: name, status: null }
            },
            domainFromUrl(url) {
                try {
                    const parsed = new URL(url)
                    return parsed.hostname.replace(/^www\./, "")
                } catch (e) {
                    return ""
                }
            },
            sortedSources(sources) {
                const arr = Array.isArray(sources) ? sources.slice() : []
                const withDomains = arr.map((s, idx) => {
                    const domain = (s.domain && String(s.domain).toLowerCase()) || this.domainFromUrl(s.url || s.key || "")
                    return { ...s, domain, _key: (s.url || s.key || s.domain || "") + "::" + idx }
                })

                const preferDomain =
                    (this.project && this.project.brand && this.project.brand.brand_domain && this.project.brand.brand_domain.toLowerCase()) ||
                    (this.brand && this.brand.brand_domain && this.brand.brand_domain.toLowerCase()) ||
                    ""

                const preferred = preferDomain ? withDomains.filter((s) => s.domain === preferDomain) : []
                const others = withDomains.filter((s) => !preferDomain || s.domain !== preferDomain)
                return preferred.concat(others).slice(0, 5)
            },
            extraSourceCount(sources) {
                const len = Array.isArray(sources) ? sources.length : 0
                return len > 5 ? len - 5 : 0
            },
            buildPlatformDeltaRows(totalRows, newRows, lostRows) {
                const totalList = Array.isArray(totalRows) ? totalRows : []
                const newList = Array.isArray(newRows) ? newRows : []
                const lostList = Array.isArray(lostRows) ? lostRows : []

                const countByPlatform = (rows) => {
                    const map = new Map()
                    rows.forEach((item) => {
                        const key = (item?.platform || "unknown").toString().trim().toLowerCase() || "unknown"
                        map.set(key, (map.get(key) || 0) + 1)
                    })
                    return map
                }

                const totalMap = countByPlatform(totalList)
                const newMap = countByPlatform(newList)
                const lostMap = countByPlatform(lostList)

                const baseOrder = Array.isArray(this.platforms) ? this.platforms.map((p) => String(p).toLowerCase()) : []
                if (this.project?.google && !baseOrder.includes("google")) baseOrder.push("google")
                const unionKeys = new Set([...totalMap.keys(), ...newMap.keys(), ...lostMap.keys()])
                const orderedKeys = [...baseOrder, ...Array.from(unionKeys).filter((k) => !baseOrder.includes(k))]

                const rows = orderedKeys
                    .filter((key) => unionKeys.has(key))
                    .map((key) => {
                        const total = totalMap.get(key) || 0
                        const newCount = newMap.get(key) || 0
                        const lostCount = lostMap.get(key) || 0
                        const info = this.getPlatformInfo(key)
                        return {
                            key,
                            label: info.label || key,
                            logo: info.logo || null,
                            total,
                            newCount,
                            lostCount
                        }
                    })
                    .sort((a, b) => {
                        if (b.total !== a.total) return b.total - a.total
                        if (b.newCount !== a.newCount) return b.newCount - a.newCount
                        if (b.lostCount !== a.lostCount) return b.lostCount - a.lostCount
                        return String(a.label || a.key).localeCompare(String(b.label || b.key))
                    })

                const maxTotal = Math.max(1, ...rows.map((r) => r.total))
                const maxNew = Math.max(1, ...rows.map((r) => r.newCount))
                const maxLost = Math.max(1, ...rows.map((r) => r.lostCount))
                const scalePct = (value, max) => {
                    if (!value || value <= 0) return 0
                    const pct = (value / max) * 100
                    return Math.max(5, Math.min(100, pct))
                }

                this.platformDeltaRows = rows.map((r) => ({
                    ...r,
                    totalPct: scalePct(r.total, maxTotal),
                    newPct: scalePct(r.newCount, maxNew),
                    lostPct: scalePct(r.lostCount, maxLost)
                }))
                this.$emit("platformOverviewUpdated", this.platformDeltaRows)
            },
            onRowInteraction(item) {
                if (!item) return
                const projectId = this.project?._id || this.projectId || this.$route?.params?.id
                if (!projectId) return
                const query = {}
                if (item.task_id || item.taskId) query.task_id = item.task_id || item.taskId
                if (item.prompt) query.prompt = item.prompt
                if (item.platform) query.platform = item.platform
                const routeBrand = this.getRouteBrandPayload()
                if (routeBrand) query.brand = routeBrand
                const dateRange = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                if (dateRange.length) {
                    query.dates = JSON.stringify(dateRange)
                } else if (this.fromDate || this.toDate) {
                    const toDay = (d) => {
                        if (!d) return null
                        const dt = new Date(d)
                        return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10)
                    }
                    const fromDay = toDay(this.fromDate)
                    const toDayVal = toDay(this.toDate) || fromDay
                    const fallbackRange = [fromDay, toDayVal].filter(Boolean)
                    if (fallbackRange.length) query.dates = JSON.stringify(fallbackRange)
                }
                // Bevorzugt als Overlay im AIProject öffnen (kein Routenwechsel, View bleibt erhalten)
                if (this.aiSuiteOpenPromptDetail) {
                    this.aiSuiteOpenPromptDetail({ ...query, project_id: projectId })
                    return
                }
                this.$router.push({
                    name: "PromptDetail",
                    params: { id: projectId },
                    query
                })
            },
            getRouteBrandPayload() {
                const matched = this.findMatchedBrandByProjectName()
                if (!matched) return null

                try {
                    return JSON.stringify({
                        canonicalName: matched.canonicalName || matched.brand_name || matched.brand || matched.name || null,
                        brand_name: matched.brand_name || matched.brand || matched.canonicalName || matched.name || null,
                        brand: matched.brand || matched.brand_name || matched.canonicalName || matched.name || null,
                        mentions: Array.isArray(matched.mentions) ? matched.mentions : []
                    })
                } catch (e) {
                    return null
                }
            },
            findMatchedBrandByProjectName() {
                const projectBrandName = this.project?.brand?.brand_name || this.brand?.brand_name || this.brand?.name || ""
                const projectToken = this.normalizeToken(projectBrandName)
                if (!projectToken || !Array.isArray(this.brands) || !this.brands.length) return null

                const getMentionValue = (mention) => {
                    if (mention == null) return ""
                    if (typeof mention === "string" || typeof mention === "number") return String(mention)
                    return mention?.text?.content || mention?.content || mention?.canonicalName || mention?.name || mention?.brand || mention?.entity || ""
                }

                return (
                    this.brands.find((entry) => {
                        const mentions = Array.isArray(entry?.mentions) ? entry.mentions : []
                        return mentions.some((mention) => this.normalizeToken(getMentionValue(mention)) === projectToken)
                    }) || null
                )
            },
            normalizeToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            getProjectBrandTokens() {
                const tokens = new Set()
                const add = (value) => {
                    const token = this.normalizeToken(value)
                    if (token) tokens.add(token)
                }
                const sources = [this.brand, this.project?.brand]
                sources.forEach((src) => {
                    if (!src) return
                    add(src.canonicalName)
                    add(src.canonical_name)
                    add(src.brand_name)
                    add(src.brandName)
                    add(src.brand)
                    add(src.name)
                    if (Array.isArray(src.mentions)) {
                        src.mentions.forEach((m) => {
                            if (typeof m === "string") add(m)
                            else if (m) {
                                add(m?.text?.content)
                                add(m?.content)
                                add(m?.name)
                                add(m?.canonicalName)
                            }
                        })
                    }
                })
                return tokens
            },
            itemHasProjectBrand(item) {
                if (!item || !Array.isArray(item.mergedEntities)) return false
                const tokens = this.getProjectBrandTokens()
                if (!tokens.size) return false
                return item.mergedEntities.some((entity) => {
                    const names = [entity?.canonicalName, entity?.name, entity?.brand_name]
                    if (names.some((n) => tokens.has(this.normalizeToken(n)))) return true
                    if (Array.isArray(entity?.mentions)) {
                        return entity.mentions.some((m) => {
                            const raw = typeof m === "string" ? m : m?.text?.content || m?.content || ""
                            return tokens.has(this.normalizeToken(raw))
                        })
                    }
                    return false
                })
            },
            normalizeMentionKey(item) {
                const platform = (item?.platform || "").toString().trim().toLowerCase()
                const promptText = (item?.prompt || "").toString().trim().toLowerCase().replace(/\s+/g, " ")
                if (promptText) return `${platform}::${promptText}`
                const mention = this.getDeltaMentionText(item).toString().trim().toLowerCase().replace(/\s+/g, " ")
                return `${platform}::${mention}`
            },
            getDeltaMentionText(item) {
                return item?.mention || item?.responseText || item?.response_text || item?.response || item?.answer || item?.content || item?.fullText || item?.text || ""
            },
            getMentionSnippet(item) {
                if (!item || !Array.isArray(item.mergedEntities)) return "-"

                const sanitizeForMentionMatch = (text) => {
                    if (!text) return ""
                    let out = String(text)
                    out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
                    out = out.replace(/(https?:\/\/[^\s)]+|www\.[^\s)]+)/gi, "")
                    return out
                }

                const isMentionMatch = (entityName, mentionsArr) => {
                    if (!entityName || !Array.isArray(mentionsArr)) return false
                    const lc = entityName.toLowerCase()
                    return mentionsArr.some((m) => {
                        if (typeof m === "string") return m.toLowerCase() === lc
                        if (m && m.text && m.text.content) return m.text.content.toLowerCase() === lc
                        return false
                    })
                }

                const match = item.mergedEntities.find((e) => e.name && isMentionMatch(e.name, this.brand?.mentions))
                if (!match) return "-"

                const responseText = item.responseText || item.response_text || item.response || item.answer || item.content || item.fullText || item.text || item.prompt || ""
                if (typeof responseText !== "string" || !responseText.trim().length) return "-"

                // Bilder (z. B. ChatGPT-Shopping-Karussell) gehoeren nicht in den Snippet:
                // verlinkte Bilder [![alt](img)](url) und reine Bilder ![alt](img) komplett entfernen
                const textWithoutImages = responseText.replace(/\[!\[[^\]]*\]\([^)]*\)\]\([^)]*\)/g, "").replace(/!\[[^\]]*\]\([^)]*\)/g, "")

                const rawMentions = Array.isArray(this.brand?.mentions) ? this.brand.mentions.slice() : []
                if (match.name && !rawMentions.includes(match.name)) rawMentions.unshift(match.name)
                const normMentions = rawMentions.map((m) => (typeof m === "string" ? m : m?.text?.content || m?.content || "")).filter((m) => m && m.trim().length)
                if (!normMentions.length) return "-"

                const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                const mentionRe = new RegExp(`(?:^|[^\\p{L}\\p{N}_])(?:${normMentions.map(escapeRe).join("|")})(?=$|[^\\p{L}\\p{N}_])`, "iu")
                const containsMention = (text) => {
                    if (!text) return false
                    return mentionRe.test(sanitizeForMentionMatch(text))
                }

                const lines = textWithoutImages.split("\n")
                const isListLine = (line) => /^\s*(?:[-*+]|\d+\.|\d+\))\s+/.test(line)
                const isTableLine = (line) => /^\s*\|.*\|\s*$/.test(line) || /\s\|\s/.test(line)

                // Finde die ERSTE Zeile, die die Brand enthaelt
                let hitIdx = -1
                for (let i = 0; i < lines.length; i++) {
                    if (containsMention(lines[i])) {
                        hitIdx = i
                        break
                    }
                }
                if (hitIdx === -1) return "-"

                const hitLine = lines[hitIdx]
                const ensure = (s) => (s && containsMention(s) ? s : null)

                if (isTableLine(hitLine)) {
                    return ensure(hitLine.trim()) || "-"
                }

                let listStartIdx = -1
                if (isListLine(hitLine)) {
                    listStartIdx = hitIdx
                } else if (/^\s+/.test(hitLine)) {
                    for (let j = hitIdx - 1; j >= 0; j--) {
                        if (isListLine(lines[j])) {
                            listStartIdx = j
                            break
                        }
                        if (lines[j].trim() === "") break
                    }
                }
                if (listStartIdx !== -1) {
                    const chunk = [lines[listStartIdx]]
                    for (let k = listStartIdx + 1; k < lines.length; k++) {
                        const l = lines[k]
                        if (l.trim() === "") break
                        if (isListLine(l)) break
                        if (!/^\s+/.test(l)) break
                        chunk.push(l)
                    }
                    const listSnippet = ensure(chunk.join("\n").trim())
                    if (listSnippet) return listSnippet
                }

                let paraStart = hitIdx
                while (paraStart > 0 && lines[paraStart - 1].trim() !== "") paraStart--
                let paraEnd = hitIdx
                while (paraEnd < lines.length - 1 && lines[paraEnd + 1].trim() !== "") paraEnd++
                const paragraph = lines
                    .slice(paraStart, paraEnd + 1)
                    .join(" ")
                    .trim()
                const sentences = paragraph.match(/[^.!?\n]+[.!?]+|[^.!?\n]+$/g) || []
                const hitSentence = sentences.find((s) => containsMention(s))
                if (hitSentence) return hitSentence.trim()

                return ensure(hitLine.trim()) || "-"
            },
            renderMentionMarkdown(item) {
                let snippet = this.getMentionSnippet(item)
                if (!snippet || snippet === "-") return "-"

                snippet = snippet.replace(/\*\*(.*?)\*\*/g, "$1")

                const urlPlaceholders = []
                const replaceUrl = (url) => {
                    const key = `__URL_PLACEHOLDER_${urlPlaceholders.length}__`
                    urlPlaceholders.push({ key, url })
                    return key
                }

                const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
                snippet = snippet.replace(linkRegex, (match, text, url) => `[${text}](${replaceUrl(url)})`)

                const bareUrlRegex = /(https?:\/\/[^\s)]+|www\.[^\s)]+)/gi
                snippet = snippet.replace(bareUrlRegex, (url) => replaceUrl(url))

                const extra = []
                if (item.mergedEntities && Array.isArray(item.mergedEntities)) {
                    const match = item.mergedEntities.find((e) => e.name && (this.brand?.mentions || []).some((m) => (typeof m === "string" ? m.toLowerCase() === e.name.toLowerCase() : false)))
                    if (match && match.name && !extra.includes(match.name)) extra.push(match.name)
                }
                const rawTerms = [...(Array.isArray(this.brand?.mentions) ? this.brand.mentions : []), ...extra]
                const terms = Array.from(
                    new Set(
                        rawTerms
                            .map((mention) => (typeof mention === "string" ? mention : mention?.text?.content || mention?.content || ""))
                            .map((s) => (s || "").trim())
                            .filter(Boolean)
                    )
                ).sort((a, b) => b.length - a.length)

                if (terms.length) {
                    const alternation = terms.map((t) => this.escapeRegExp(t)).join("|")
                    const regex = new RegExp(`\\b(${alternation})\\b`, "gi")
                    snippet = snippet.replace(regex, "**$1**")
                }

                urlPlaceholders.forEach(({ key, url }) => {
                    snippet = snippet.replace(new RegExp(this.escapeRegExp(key), "g"), url)
                })

                return this.md.render(snippet)
            },
            escapeRegExp(string) {
                return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            },
            rowToDeltaView(item) {
                const brandsText = Array.isArray(item?.mergedEntities)
                    ? item.mergedEntities
                          .map((e) => e.canonicalName || e.name || "")
                          .filter(Boolean)
                          .join(", ")
                    : ""
                const sourcesText = Array.isArray(item?.sources)
                    ? item.sources
                          .map((s) => s?.domain || s?.url || s?.key || "")
                          .filter(Boolean)
                          .join(", ")
                    : ""
                return {
                    ...item,
                    mention: item?.mention || this.getDeltaMentionText(item),
                    brandsText,
                    sourcesText,
                    sources: Array.isArray(item?.sources) ? item.sources : []
                }
            },
            async fetchMentionsSnapshot(day) {
                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/promptresponses/by-brand"

                const payload = {
                    company_id: localStorage.company_id,
                    project_id: this.projectId,
                    brand: this.brand,
                    skip: 0,
                    limit: 5000,
                    sort: { importDate: -1 },
                    fromDate: day,
                    toDate: day
                }

                if (this.selectedPlatform?.key && this.selectedPlatform?.key !== "all") payload.platform = this.selectedPlatform.key
                if (Array.isArray(this.tags) && this.tags.length) {
                    payload.tags = this.tags
                    payload.tagsOperator = this.tagOperator
                }
                if (this.search) payload.search = this.search
                if (Array.isArray(this.initialFilters) && this.initialFilters.length) payload.filters = this.initialFilters

                const res = await this.$helpers.axiosPost(url, payload)
                const rows = Array.isArray(res?.data?.responses) ? res.data.responses : []
                return rows.filter((item) => this.itemHasProjectBrand(item))
            },
            async fetchMentionDeltas() {
                const fallbackStart = Array.isArray(this.selectedDates) ? this.selectedDates[0] : ""
                const fallbackEnd = Array.isArray(this.selectedDates) ? this.selectedDates[1] || this.selectedDates[0] : ""
                const startDay = this.fromDate || fallbackStart
                const endDay = this.toDate || fallbackEnd || startDay
                if (!this.initialFiltersReady || !this.projectId || !this.brand || !startDay || !endDay) return
                this.deltaLoading = true
                this.$emit("deltaMetrics", {
                    total: this.totalMentionsCount,
                    newCount: this.newMentionsRows.length,
                    lostCount: this.lostMentionsRows.length,
                    loading: true
                })
                try {
                    const [startRows, endRows] = await Promise.all([this.fetchMentionsSnapshot(startDay), this.fetchMentionsSnapshot(endDay)])
                    this.totalMentionsCount = endRows.length

                    const startMap = new Map()
                    startRows.forEach((item) => {
                        const key = this.normalizeMentionKey(item)
                        if (key && !startMap.has(key)) startMap.set(key, item)
                    })
                    const endMap = new Map()
                    endRows.forEach((item) => {
                        const key = this.normalizeMentionKey(item)
                        if (key && !endMap.has(key)) endMap.set(key, item)
                    })

                    const newRows = []
                    endMap.forEach((item, key) => {
                        if (!startMap.has(key)) newRows.push(this.rowToDeltaView(item))
                    })

                    const lostRows = []
                    startMap.forEach((item, key) => {
                        if (!endMap.has(key)) lostRows.push(this.rowToDeltaView(item))
                    })

                    this.newMentionsRows = newRows
                    this.lostMentionsRows = lostRows
                    this.buildPlatformDeltaRows(endRows, newRows, lostRows)
                    this.$emit("deltaMetrics", {
                        total: this.totalMentionsCount,
                        newCount: this.newMentionsRows.length,
                        lostCount: this.lostMentionsRows.length,
                        loading: false,
                        signature: this.deltaInputSignature
                    })
                } catch (e) {
                    this.newMentionsRows = []
                    this.lostMentionsRows = []
                    this.totalMentionsCount = 0
                    this.platformDeltaRows = []
                    this.$emit("platformOverviewUpdated", [])
                    this.$emit("deltaMetrics", {
                        total: 0,
                        newCount: 0,
                        lostCount: 0,
                        loading: false
                    })
                } finally {
                    this.deltaLoading = false
                }
            },
            async exportTableData() {
                if (this.activeSubTab === "all") {
                    const table = this.$refs.table
                    if (table && typeof table.exportTableData === "function") {
                        return table.exportTableData()
                    }
                    return ""
                }

                const headers = this.deltaHeaders.map((h) => h.text)
                const rows = (this.activeSubTab === "new" ? this.newMentionsRows : this.lostMentionsRows).map((item) => [
                    item.platform || "",
                    `"${(item.mention || "").toString().replace(/"/g, '""')}"`,
                    `"${(item.brandsText || "").toString().replace(/"/g, '""')}"`,
                    `"${(item.sourcesText || "").toString().replace(/"/g, '""')}"`,
                    item.importDate || ""
                ])
                return [headers, ...rows].map((r) => r.join(";")).join("\n")
            }
        },
        mounted() {
            this.scheduleDeltaRefresh()
        }
    }
</script>

<style>
    #mentionTableDeltaNew th,
    #mentionTableDeltaLost th {
        white-space: nowrap !important;
        text-transform: uppercase !important;
        font-weight: 300 !important;
    }
    .mentions-breakdown-wrap .v-card__title {
        padding-bottom: 0 !important;
    }
    .platform-delta-legend {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
        margin-bottom: 14px;
        font-size: 12px;
        color: rgba(15, 23, 42, 0.8);
    }
    .platform-delta-legend .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-weight: 600;
    }
    .legend-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;
    }
    .legend-dot--total {
        background: #64748b;
    }
    .legend-dot--new {
        background: #1f9d45;
    }
    .legend-dot--lost {
        background: #d64545;
    }
    .platform-delta-rows {
        display: grid;
        gap: 8px;
    }
    .platform-delta-grid-head {
        display: grid;
        grid-template-columns: 180px 1fr 1fr 1fr;
        gap: 12px;
        padding: 0 10px 8px;
        margin-bottom: 6px;
        border-bottom: 1px solid rgba(15, 23, 42, 0.08);
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        font-weight: 700;
        color: rgba(15, 23, 42, 0.55);
    }
    .platform-delta-row {
        display: grid;
        grid-template-columns: 180px 1fr 1fr 1fr;
        gap: 12px;
        align-items: center;
        padding: 10px;
        border-radius: 10px;
        border: 1px solid rgba(15, 23, 42, 0.06);
        background: rgba(255, 255, 255, 0.55);
    }
    .platform-delta-label {
        display: flex;
        align-items: center;
        gap: 8px;
        font-weight: 700;
        color: rgba(15, 23, 42, 0.85);
    }
    .platform-delta-name {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .platform-delta-metric-cell {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        cursor: pointer;
        border-radius: 8px;
        padding: 2px 4px;
        transition: background-color 0.18s ease;
    }
    .platform-delta-metric-cell:hover {
        background: rgba(15, 23, 42, 0.05);
    }
    .platform-delta-metric-cell--active {
        background: rgba(37, 99, 235, 0.1);
    }
    .platform-delta-metric-value {
        width: 36px;
        text-align: right;
        font-size: 12px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: rgba(15, 23, 42, 0.82);
    }
    .platform-delta-bar {
        position: relative;
        height: 9px;
        flex: 1;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.08);
        overflow: hidden;
    }
    .platform-delta-bar-fill {
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        border-radius: 999px;
    }
    .platform-delta-bar-fill--total {
        background: #64748b;
    }
    .platform-delta-bar-fill--new {
        background: #1f9d45;
    }
    .platform-delta-bar-fill--lost {
        background: #d64545;
    }
    .reset-platform-filter-btn.v-btn {
        text-transform: none;
        letter-spacing: 0.2px;
        font-weight: 600;
        box-shadow: 0 2px 8px rgba(var(--v-primary-base-rgb, 25, 118, 210), 0.35) !important;
        border: 1px solid currentColor;
    }
    .reset-platform-filter-btn.v-btn:hover {
        filter: brightness(1.05);
    }
    .theme--dark .platform-delta-label,
    .theme--dark .platform-delta-legend {
        color: rgba(255, 255, 255, 0.85);
    }
    .theme--dark .platform-delta-grid-head {
        border-bottom-color: rgba(255, 255, 255, 0.14);
        color: rgba(255, 255, 255, 0.58);
    }
    .theme--dark .platform-delta-row {
        background: rgba(255, 255, 255, 0.03);
        border-color: rgba(255, 255, 255, 0.12);
    }
    .theme--dark .platform-delta-metric-cell:hover {
        background: rgba(255, 255, 255, 0.06);
    }
    .theme--dark .platform-delta-metric-cell--active {
        background: rgba(96, 165, 250, 0.18);
    }
    .theme--dark .platform-delta-bar {
        background: rgba(255, 255, 255, 0.15);
    }
    .theme--dark .platform-delta-metric-value {
        color: rgba(255, 255, 255, 0.82);
    }
    @media (max-width: 960px) {
        .platform-delta-grid-head {
            display: none;
        }
        .platform-delta-row {
            grid-template-columns: 1fr;
            gap: 8px;
        }
    }
</style>
